/**
 * The mapping step — shown when the uploaded file's headers don't already
 * match the template. One row per file column, each with a dropdown
 * pointing at a template field (or "ignore"). Mapping is one-to-one:
 * picking a field clears it from whichever column held it before.
 */
import { type ColumnSchema, templateHeaders } from "./column-schema.ts";

interface ColumnMappingStepProps {
  schema: ColumnSchema;
  fileName: string;
  fileHeaders: string[];
  columnMap: Record<string, string>;
  onColumnMapChange: (next: Record<string, string>) => void;
}

export function ColumnMappingStep({
  schema,
  fileName,
  fileHeaders,
  columnMap,
  onColumnMapChange,
}: ColumnMappingStepProps) {
  const mappedCanonical = new Set(Object.values(columnMap).filter(Boolean));
  const missingRequired = schema.requiredHeaders.filter((r) => !mappedCanonical.has(r));

  const options: Array<{ value: string; label: string }> = [
    { value: "", label: "— Ignore this column —" },
    ...templateHeaders(schema).map((canonical) => ({
      value: canonical,
      label: `${schema.headerLabel[canonical] ?? canonical}${
        schema.requiredHeaders.includes(canonical) ? " *" : ""
      }`,
    })),
  ];

  return (
    <div className="mt-4 space-y-3">
      <div className="text-xs text-muted">
        <strong className="text-ink">{fileName}</strong> · {fileHeaders.length} column
        {fileHeaders.length === 1 ? "" : "s"} detected · required fields marked *
      </div>
      {missingRequired.length > 0 ? (
        <div className="rounded-sm border border-warn bg-warn/10 px-3 py-2 text-xs text-warn">
          <strong>Still to map:</strong> {missingRequired.join(", ")}
        </div>
      ) : (
        <div className="rounded-sm border border-ok bg-ok/10 px-3 py-2 text-xs text-ok">
          All required fields resolved. Continue to preview.
        </div>
      )}
      <div className="max-h-72 overflow-auto rounded-sm border border-line">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-bg text-muted">
            <tr>
              <th className="px-2 py-1 text-left">File column</th>
              <th className="px-2 py-1 text-left">→ Template field</th>
            </tr>
          </thead>
          <tbody>
            {fileHeaders.map((h) => (
              <tr key={h} className="border-t border-line">
                <td className="px-2 py-1 font-mono text-xs">{h}</td>
                <td className="px-2 py-1">
                  <select
                    value={columnMap[h] ?? ""}
                    onChange={(e) => {
                      const next = { ...columnMap };
                      // Clear any other file column mapped to the same
                      // canonical field — the mapping is one-to-one.
                      if (e.target.value) {
                        for (const [key, val] of Object.entries(next)) {
                          if (val === e.target.value && key !== h) next[key] = "";
                        }
                      }
                      next[h] = e.target.value;
                      onColumnMapChange(next);
                    }}
                    className="h-8 w-full rounded-sm border border-line bg-paper px-2 text-xs"
                  >
                    {options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
